import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

export interface ProductPlatformMapping {
  id: string;
  productId: string;
  platform: 'grab';
  platformItemId: string;
  platformCategoryId?: string;
  syncEnabled: boolean;
  lastSyncedAt?: string;
  syncStatus?: 'pending' | 'synced' | 'failed';
  syncError?: string;
}

export interface GrabIntegrationConfig {
  id: string;
  type: 'grab';
  environment: 'sandbox' | 'staging' | 'production';
  clientId: string;
  partnerId: string;
  partnerMerchantId: string;
  webhookUrl: string;
  isActive: boolean;
  storeId?: string;
  currency?: string;
  currencyExponent?: number;
}

export interface GrabMenuSync {
  id: string;
  syncId: string;
  status: 'pending' | 'processing' | 'success' | 'failed' | 'partial';
  startedAt: string;
  completedAt?: string;
  itemsSynced: number;
  itemsTotal: number;
  itemsFailed: number;
  errors?: Array<{
    itemId: string;
    productId?: string;
    error: string;
  }>;
}

class GrabService {
  private apiRoot = configs.apiRoot;

  // Product Platform Mappings
  async getMappings(params?: {
    productId?: string;
    platform?: 'grab';
    syncEnabled?: boolean;
  }): Promise<ProductPlatformMapping[]> {
    const queryParams = new URLSearchParams();
    if (params?.productId) {
      queryParams.set('productId', params.productId);
    }
    if (params?.platform) {
      queryParams.set('platform', params.platform);
    }
    if (params?.syncEnabled !== undefined) {
      queryParams.set('syncEnabled', params.syncEnabled.toString());
    }

    const response = await authenticatedFetch(
      `${this.apiRoot}/product_platform_mappings${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch mappings: ${response.statusText}`);
    }

    const result = await response.json();
    return Array.isArray(result) ? result : result.data || [];
  }

  async getMapping(id: string): Promise<ProductPlatformMapping> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_platform_mappings/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch mapping: ${response.statusText}`);
    }

    return await response.json();
  }

  async createMapping(
    data: Omit<ProductPlatformMapping, 'id'>
  ): Promise<ProductPlatformMapping> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_platform_mappings`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(
        error.message || `Failed to create mapping: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async updateMapping(
    id: string,
    data: Partial<ProductPlatformMapping>
  ): Promise<ProductPlatformMapping> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_platform_mappings/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(
        error.message || `Failed to update mapping: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async deleteMapping(id: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/product_platform_mappings/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete mapping: ${response.statusText}`);
    }
  }

  async enableSync(mappingId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/mappings/${mappingId}/enable`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error(`Failed to enable sync: ${response.statusText}`);
    }
  }

  async disableSync(mappingId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/mappings/${mappingId}/disable`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error(`Failed to disable sync: ${response.statusText}`);
    }
  }

  // Grab Integration Config
  async getConfigs(): Promise<GrabIntegrationConfig[]> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab_integration_configs`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch configs: ${response.statusText}`);
    }

    const result = await response.json();
    return Array.isArray(result) ? result : result.data || [];
  }

  async getActiveConfig(): Promise<GrabIntegrationConfig | null> {
    const configs = await this.getConfigs();
    return configs.find((c) => c.isActive) || null;
  }

  async createConfig(
    data: Omit<GrabIntegrationConfig, 'id'>
  ): Promise<GrabIntegrationConfig> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab_integration_configs`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(
        error.message || `Failed to create config: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async updateConfig(
    id: string,
    data: Partial<GrabIntegrationConfig>
  ): Promise<GrabIntegrationConfig> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab_integration_configs/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(
        error.message || `Failed to update config: ${response.statusText}`
      );
    }

    return await response.json();
  }

  // Menu Sync
  async syncMenu(): Promise<{ syncId: string; status: string }> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/menu/sync`,
      { method: 'POST' }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(
        error.message || `Failed to sync menu: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async syncProduct(productId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/menu/sync/${productId}`,
      { method: 'POST' }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(
        error.message || `Failed to sync product: ${response.statusText}`
      );
    }
  }

  async getMenuSyncs(): Promise<GrabMenuSync[]> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab_menu_syncs`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch menu syncs: ${response.statusText}`);
    }

    const result = await response.json();
    return Array.isArray(result) ? result : result.data || [];
  }

  async getMenuSync(id: string): Promise<GrabMenuSync> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab_menu_syncs/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch menu sync: ${response.statusText}`);
    }

    return await response.json();
  }

  // Order Management
  async acceptOrder(orderId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/orders/${orderId}/accept`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error(`Failed to accept order: ${response.statusText}`);
    }
  }

  async rejectOrder(orderId: string, reason?: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/orders/${orderId}/reject`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to reject order: ${response.statusText}`);
    }
  }

  async markOrderReady(orderId: string, readyTime?: Date): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/orders/${orderId}/ready`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ readyTime: readyTime?.toISOString() }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to mark order ready: ${response.statusText}`);
    }
  }

  async cancelOrder(orderId: string, cancelCode?: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/grab/orders/${orderId}/cancel`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cancelCode }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to cancel order: ${response.statusText}`);
    }
  }
}

export const grabService = new GrabService();
